import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Chip,
  Divider,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
} from "@mui/material";
import { Event as EventIcon, Star as StarIcon } from "@mui/icons-material";
import { useAuth } from "../contexts/AuthContext";
import { Outfit, ClothingItem } from "../types";
import LikeButton from "../components/Outfit/LikeButton";
import ShareMenu from "../components/Outfit/ShareMenu";
import TryOnButton from "../components/Outfit/TryOnButton";
import TryOnModal from "../components/Outfit/TryOnModal";
import HowToWearThis from "../components/Outfit/HowToWearThis";
import StyleNotes from "../components/Outfit/StyleNotes";
import RelatedOutfits from "../components/Outfit/RelatedOutfits";
import EditableDescription from "../components/Outfit/EditableDescription";
import SEOHead from "../components/SEO/SEOHead";
import RatingForm from "../components/Rating/RatingForm";
import StarRating from "../components/Rating/StarRating";
import { RatingService } from "../services/rating-service";
import OutfitService from "../services/outfit-service";
import ContentEnrichService, { OutfitEnrichment } from "../services/content-enrich-service";
import { checkLiked } from "../services/social-service";
import { targetingService } from "../services/TargetingService";
import NativeInFeedAd from "../components/Ads/NativeInFeedAd";

/**
 * Outfit detail page component
 */
const OutfitDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { isAuthenticated, user } = useAuth();

  // State
  const [outfit, setOutfit] = useState<Outfit | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isLiked, setIsLiked] = useState<boolean>(false);
  const [showRatingForm, setShowRatingForm] = useState<boolean>(false);
  const [userRating, setUserRating] = useState<number | null>(null);
  const [outfitTargeting, setOutfitTargeting] = useState<any>({});
  const [tryOnModalOpen, setTryOnModalOpen] = useState(false);
  const [tryOnSessionId, setTryOnSessionId] = useState<string | null>(null);
  const [enrichment, setEnrichment] = useState<OutfitEnrichment | null>(null);

  // Fetch outfit data
  useEffect(() => {
    const fetchOutfit = async () => {
      if (!id) return;

      try {
        setIsLoading(true);
        setError(null);

        // Data fetched from backend API to get outfit data
        const outfitData = await OutfitService.getOutfitById(id);
        setOutfit(outfitData);

        // Set like status from the backend response (get-outfit returns isLiked for authenticated users)
        if ((outfitData as any).isLiked) {
          setIsLiked(true);
        }

        // Also check like status via dedicated endpoint for reliability
        if (isAuthenticated && user) {
          try {
            const liked = await checkLiked(outfitData.id);
            setIsLiked(liked);
          } catch (likeError) {
            // Silently fail — use the isLiked from outfit response
          }
        }

        // Fetch AI enrichment content (graceful degradation)
        try {
          const enrichData = await ContentEnrichService.getOutfitEnrichment(id);
          setEnrichment(enrichData);
        } catch {
          // Hide enrichment sections if API fails
        }

        // Generate contextual targeting based on outfit data
        const targeting = await targetingService.generateTargeting({
          pageContext: 'outfit-detail',
          pageType: 'outfit-detail',
          outfitData: {
            id: outfitData.id,
            userId: outfitData.userId,
            imageUrl: outfitData.imageUrl,
            description: outfitData.description,
            tags: outfitData.tags || [],
            brands: outfitData.clothingItems?.map((item: ClothingItem) => item.brand).filter(Boolean) || [],
            categories: [outfitData.eventType || 'general'],
            event: outfitData.eventType,
            aiAnalysis: outfitData.aiAnalysis ? {
              clothingItems: outfitData.aiAnalysis.detectedClothingItems?.map(item => item.type) || [],
              style: 'casual', // Default style since not available in current AI analysis
              colors: outfitData.aiAnalysis.detectedClothingItems?.flatMap(item => item.colors) || [],
              occasion: outfitData.eventType || 'general'
            } : undefined
          }
        });
        setOutfitTargeting(targeting);

        // If authenticated, get user's rating
        if (isAuthenticated && user) {
          try {
            const rating = await RatingService.getUserRating(id, user.id);
            setUserRating(rating);
          } catch (ratingError: any) {
            // Only log error if it's not a 401 (authentication error)
            if (ratingError.response?.status !== 401) {
              console.error("Error fetching user rating:", ratingError);
            }
            // For 401 errors, user is not authenticated, which is expected
            setUserRating(null);
          }
        }
      } catch (error: any) {
        console.error("Error fetching outfit:", error);
        if (error.isDeleted) {
          setError("This outfit has been removed or is no longer available.");
        } else {
          setError("Failed to load outfit. Please try again later.");
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchOutfit();
  }, [id, isAuthenticated]);

  // Handle rating submission
  const handleRateOutfit = async (outfitId: string, rating: number) => {
    if (!outfitId) return;
    
    if (!isAuthenticated) {
      // Redirect to login if not authenticated
      window.location.href = '/login';
      return;
    }

    try {
      const result = await RatingService.rateOutfit({
        outfitId,
        rating
      });
      setUserRating(rating);

      // Update outfit with new rating data
      if (outfit) {
        setOutfit({
          ...outfit,
          ratings: {
            ...outfit.ratings,
            averageRating: result.averageRating,
            totalRatings: result.totalRatings,
          },
        });
      }

      setShowRatingForm(false);
    } catch (error: any) {
      console.error("Error rating outfit:", error);
      // If authentication error, redirect to login
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
    }
  };

  // Handle like change
  const handleLikeChange = (liked: boolean) => {
    setIsLiked(liked);

    // Update outfit with new like count
    if (outfit) {
      setOutfit({
        ...outfit,
        likes: liked ? outfit.likes + 1 : outfit.likes - 1,
      });
    }
  };

  // Handle try-on workflow
  const handleTryOnStart = () => {
    console.log('Try-on started for outfit:', outfit?.id);
  };

  const handleTryOnComplete = (resultImageUrl: string) => {
    console.log('Try-on completed with result:', resultImageUrl);
  };

  const handleTryOnError = (error: string) => {
    console.error('Try-on error:', error);
  };

  const handleOpenTryOnModal = (sessionId: string) => {
    setTryOnSessionId(sessionId);
    setTryOnModalOpen(true);
  };

  const handleCloseTryOnModal = () => {
    setTryOnModalOpen(false);
    setTryOnSessionId(null);
  };

  const handleDownloadTryOnResult = (imageUrl: string) => {
    // Create download link
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = `try-on-result-${outfit?.id}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", my: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !outfit) {
    return (
      <Paper elevation={1} sx={{ p: 4, textAlign: "center", my: 4 }}>
        <Typography variant="h5" color="error" gutterBottom>
          {error || "Outfit not found"}
        </Typography>
        <Typography variant="body1">
          The outfit you&apos;re looking for could not be found or an error
          occurred.
        </Typography>
        {error?.includes('404') && (
          <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic' }}>
            If this is a recently uploaded outfit, please try refreshing the page.
          </Typography>
        )}
      </Paper>
    );
  }

  return (
    <Grid container spacing={4}>
      <SEOHead
        title={outfit.title || outfit.description || 'Outfit Detail'}
        description={outfit.description || 'View this outfit on Hooliah'}
        canonicalUrl={`https://hooliah.com/outfit/${outfit.id}`}
        ogImage={outfit.imageUrl}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'ImageObject',
          name: outfit.title || outfit.description || 'Outfit',
          description: outfit.description || '',
          contentUrl: outfit.imageUrl,
          author: { '@type': 'Person', name: outfit.userId },
        }}
      />
      {/* Outfit image */}
      <Grid item xs={12} md={6}>
        <Paper
          elevation={2}
          sx={{
            overflow: "hidden",
            borderRadius: 2,
            height: { xs: "auto", md: "calc(100vh - 200px)" },
            maxHeight: 800,
          }}
        >
          <Box
            component="img"
            src={outfit.imageUrl}
            alt={outfit.title || outfit.description || "Outfit image"}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
            }}
          />
        </Paper>

        {/* Sponsored ad below outfit image */}
        <Box sx={{ mt: 2 }}>
          <NativeInFeedAd
            index={0}
            targeting={{
              keywords: ['fashion', 'outfit-detail', ...(outfit.tags || [])],
              categories: [outfit.eventType || 'general', 'outfit-detail'],
              contextual: {
                pageType: 'outfit-detail' as any,
                fashionStyles: [],
                brands: outfit.clothingItems?.map((item: ClothingItem) => item.brand).filter(Boolean) || [],
                events: [outfit.eventType || '']
              }
            }}
          />
        </Box>
      </Grid>

      {/* Outfit details */}
      <Grid item xs={12} md={6}>
        <Box>
          {/* Event types */}
          <Box sx={{ mb: 2 }}>
            {[outfit.eventType].map((event) => (
              <Chip
                key={event}
                icon={<EventIcon />}
                label={event}
                color="primary"
                variant="outlined"
                sx={{ mr: 1, mb: 1 }}
              />
            ))}
          </Box>

          {/* Description */}
          <Typography variant="h4" gutterBottom>
            {outfit.title || "Stylish Outfit"}
          </Typography>

          {/* Rating and likes */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mr: 3 }}>
              <StarRating
                value={outfit.ratings.averageRating}
                readOnly
                size="medium"
              />
              <Typography variant="body2" sx={{ ml: 1 }}>
                ({outfit.ratings.totalRatings} ratings)
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <LikeButton
                outfitId={outfit.id}
                initialLikeCount={outfit.likes}
                initialLiked={isLiked}
                onLikeChange={handleLikeChange}
              />

              <TryOnButton
                outfitId={outfit.id}
                outfitImageUrl={outfit.imageUrl}
                size="medium"
                onTryOnStart={handleTryOnStart}
                onTryOnComplete={handleTryOnComplete}
                onTryOnError={handleTryOnError}
                onOpenModal={handleOpenTryOnModal}
              />

              <ShareMenu outfitId={outfit.id} size="small" />
            </Box>
          </Box>

          {/* Rate button */}
          {isAuthenticated && !showRatingForm && (
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setShowRatingForm(true)}
              startIcon={<StarIcon />}
              sx={{ mb: 3 }}
            >
              {userRating ? "Update Rating" : "Rate This Outfit"}
            </Button>
          )}
          
          {/* Login prompt for unauthenticated users */}
          {!isAuthenticated && (
            <Button
              variant="outlined"
              color="primary"
              onClick={() => window.location.href = '/login'}
              startIcon={<StarIcon />}
              sx={{ mb: 3 }}
            >
              Login to Rate This Outfit
            </Button>
          )}

          {/* Rating form */}
          {showRatingForm && (
            <Box sx={{ mb: 3 }}>
              <RatingForm
                outfitId={outfit.id}
                initialRating={userRating || 0}
                onSubmit={handleRateOutfit}
                onCancel={() => setShowRatingForm(false)}
              />
            </Box>
          )}

          <Divider sx={{ my: 3 }} />

          {/* AI Analysis */}
          {outfit.aiAnalysis && (
            <>
              <Typography variant="h6" gutterBottom>
                AI Fashion Analysis
              </Typography>
              
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="body1" paragraph>
                    {outfit.aiAnalysis.description}
                  </Typography>
                  
                  {outfit.aiAnalysis.highlights && outfit.aiAnalysis.highlights.length > 0 && (
                    <>
                      <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                        Style Highlights:
                      </Typography>
                      <List dense>
                        {outfit.aiAnalysis.highlights.map((highlight, index) => (
                          <ListItem key={index}>
                            <ListItemText primary={`• ${highlight}`} />
                          </ListItem>
                        ))}
                      </List>
                    </>
                  )}
                  
                  {outfit.aiAnalysis.suggestedTags && outfit.aiAnalysis.suggestedTags.length > 0 && (
                    <>
                      <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                        AI Suggested Tags:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {outfit.aiAnalysis.suggestedTags.map((tag, index) => (
                          <Chip
                            key={index}
                            label={tag}
                            size="small"
                            color="secondary"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </>
                  )}
                </CardContent>
              </Card>
              
              <Divider sx={{ my: 3 }} />
            </>
          )}

          {/* Clothing items */}
          <Typography variant="h6" gutterBottom>
            Clothing Items
          </Typography>

          <Grid container spacing={2}>
            {outfit.clothingItems?.map((item: ClothingItem, index: number) => (
              <Grid item xs={12} sm={6} key={index}>
                <Card variant="outlined" sx={{ backgroundColor: "#E3F2FD", color: "#1C1C1C" }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      {item.type}
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemText
                          primary="Brand"
                          secondary={item.brand}
                          primaryTypographyProps={{ variant: "body2" }}
                          secondaryTypographyProps={{ variant: "body1" }}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Color"
                          secondary={item.color}
                          primaryTypographyProps={{ variant: "body2" }}
                          secondaryTypographyProps={{ variant: "body1" }}
                        />
                      </ListItem>
                      {item.description && (
                        <ListItem>
                          <ListItemText
                            primary="Description"
                            secondary={item.description}
                            primaryTypographyProps={{ variant: "body2" }}
                            secondaryTypographyProps={{ variant: "body1" }}
                          />
                        </ListItem>
                      )}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Tags */}
          {outfit.tags && outfit.tags.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Tags
              </Typography>
              <Box>
                {outfit.tags.map((tag: string) => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    sx={{ mr: 1, mb: 1 }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Editable description (owner only) */}
          <EditableDescription
            description={outfit.description || ''}
            isOwner={isAuthenticated && user?.id === outfit.userId}
            onSave={async () => { /* save via API */ }}
          />

          {/* AI Enrichment sections — graceful degradation */}
          {enrichment?.howToWear && <HowToWearThis suggestions={enrichment.howToWear} />}
          {enrichment?.styleNotes && <StyleNotes notes={enrichment.styleNotes} />}
          {enrichment?.relatedOutfits && <RelatedOutfits outfits={enrichment.relatedOutfits} />}
        </Box>
      </Grid>

      {/* Try-On Modal */}
      {tryOnSessionId && (
        <TryOnModal
          isOpen={tryOnModalOpen}
          sessionId={tryOnSessionId}
          onClose={handleCloseTryOnModal}
          onDownload={handleDownloadTryOnResult}
          outfit={outfit}
        />
      )}
    </Grid>
  );
};

export default OutfitDetailPage;
